import { type DisciplineOption, MEET_BULK_ACTION_MAX, type MeetBulkActionResult, type MeetListItem } from "@seagames/contracts";
import { FilterBar, FilterField, Select, type SelectOption, useFilterDraft } from "@seagames/ui";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { STATUS_GROUPS, type StatusGroup, groupFilter } from "../../components/dashboard/status-groups";
import { dayInZone, dayRange, isDay } from "../../components/home/day";
import { formatCalendarDay } from "../../components/home/format";
import { topLevelVenues } from "../../components/home/home-context";
import { AddMeetDrawer, type AddMeetsCreated } from "../../components/meets-list/add-meet/add-meet-drawer";
import { BulkMeetsDialog } from "../../components/meets-list/bulk-meets-dialog";
import { DisciplineCards } from "../../components/meets-list/discipline-cards";
import { MedalToggle } from "../../components/meets-list/medal-toggle";
import { disciplineOptionsQuery, eventOptionsQuery, gamesVenueOptionsQuery, meetDaysQuery } from "../../components/meets-list/queries";
import { MeetNoField } from "../../components/meets-list/meet-no-field";
import { medalParam, meetNoFromDraft, meetNoParam } from "../../components/meets-list/search";
import { daysOf, resolveDiscipline } from "../../components/meets-list/sport-page";
import { ReportsDialog } from "../../components/meets-list/reports-dialog";
import { type MeetSelection, SportMeetsTable } from "../../components/meets-list/sport-meets-table";
import { StandingsPicker } from "../../components/meets-list/standings-picker";
import { Card } from "../../components/page/card";
import { SheetIcon } from "../../components/print/print-icons";
import { FilterPanel } from "../../components/page/filter-panel";
import { PageHeader } from "../../components/page/page-header";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { errorMessage } from "../../lib/api/messages";
import { canBulkMeets } from "../../lib/auth/access";
import { type CurrentGamesOption, gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatInteger } from "../../lib/i18n/format";
import { scheduleQuery } from "../../lib/meet";

/** How long the meets just added stay highlighted. */
const FRESH_MS = 12_000;
/** Rows per keyset page: "Next 50". Keyset pages have no total, so no page numbers. */
const PAGE = 50;

const uuid = z.string().uuid().optional().catch(undefined);
/**
 * `?discipline=<id>` picks the discipline card; the Filters card adds
 * `event`, `day` (YYYY-MM-DD, the games' zone), `status` (a dashboard group),
 * `venue` and `no` (the meet number, exactly); `medal=true` is the table's
 * "Medal events only". All in the URL so Back restores them. A bad value is
 * dropped.
 */
const sportSearch = z.object({
	discipline: uuid,
	event: uuid,
	day: z.string().refine(isDay).optional().catch(undefined),
	status: z.enum(STATUS_GROUPS).optional().catch(undefined),
	venue: uuid,
	no: meetNoParam,
	medal: medalParam,
});
type SportSearch = z.infer<typeof sportSearch>;

/** The Filters card's draft: empty strings for "any", like a form. */
interface SportDraft extends Record<string, unknown> {
	event: string;
	day: string;
	status: StatusGroup | "";
	venue: string;
	no: string;
}
const EMPTY_SPORT_DRAFT: SportDraft = { event: "", day: "", status: "", venue: "", no: "" };

/**
 * Meets › one sport — the old OVR's sport page: the sport's name and code,
 * its disciplines as cards, then for the picked one a fold-out "Filters"
 * card and the meets table, headed by Add meet, Bulk action (admins),
 * Standings & brackets, Reports and "Medal events only". The crumbs (Meets › <sport>) come from the shell,
 * which already holds the sport's name.
 */
export const Route = createFileRoute("/_app/sports/$sportId")({
	validateSearch: sportSearch,
	component: SportPage,
});

function SportPage() {
	const { sportId } = Route.useParams();
	const { t } = useTranslation("meetsList");
	const current = useCurrentGames();
	const sports = useQuery({ ...gamesSportsQuery(current.gamesId ?? ""), enabled: current.gamesId !== null });
	const sport = sports.data?.find((s) => s.sport_id === sportId);

	if (current.isLoading || sports.isPending) return <Spinner className="size-[1.5rem] text-board" label={t("sport.loading")} />;
	const error = current.error ?? sports.error;
	if (error) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("sportFallback")} />
				<EmptyState title={t("errors.gamesTitle")} body={errorMessage(error)} />
			</div>
		);
	}
	if (!current.games || !sport) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("sportFallback")} />
				<EmptyState
					title={t("sport.notInGamesTitle", { games: current.games?.name ?? "" })}
					body={t("sport.notInGamesBody")}
					action={
						<Link to="/meets" className="inline-flex min-h-touch items-center rounded-btn border border-rule bg-sheet px-[1rem] font-bold text-[0.9375rem] hover:bg-paper">
							{t("sport.allSports")}
						</Link>
					}
				/>
			</div>
		);
	}
	return <SportMeets key={sport.sport_id} games={current.games} sportId={sport.sport_id} sportName={sport.name} sportCode={sport.code} />;
}

function SportMeets({ games, sportId, sportName, sportCode }: { games: CurrentGamesOption; sportId: string; sportName: string; sportCode: string }) {
	const { t } = useTranslation("meetsList");
	const search = Route.useSearch();
	const disciplines = useQuery(disciplineOptionsQuery(sportId));
	const discipline = disciplines.data ? resolveDiscipline(disciplines.data, search.discipline) : null;

	return (
		<div className="flex flex-col">
			<PageHeader title={sportName} subtitle={t("sport.subtitle", { code: sportCode, games: games.name })} />
			{disciplines.isPending ? (
				<Spinner className="size-[1.5rem] text-board" label={t("sport.loading")} />
			) : disciplines.isError ? (
				<EmptyState
					title={t("sport.listError")}
					body={errorMessage(disciplines.error)}
					action={
						<Button variant="outline" onClick={() => void disciplines.refetch()}>
							{t("errors.retry")}
						</Button>
					}
				/>
			) : !discipline ? (
				<EmptyState title={t("sport.noDisciplinesTitle")} body={t("sport.noDisciplinesBody")} />
			) : (
				<div className="flex flex-col gap-[1.5rem]">
					<DisciplineCards sportId={sportId} disciplines={disciplines.data} current={discipline.discipline_id} />
					<DisciplineMeets
						// Another discipline starts with its own filter lists (its events, days, venues).
						key={discipline.discipline_id}
						games={games}
						sportId={sportId}
						sportName={sportName}
						disciplines={disciplines.data}
						discipline={discipline}
						search={search}
					/>
				</div>
			)}
		</div>
	);
}

/** The picked discipline: the Filters card, then the table card with its toolbar. */
function DisciplineMeets({
	games,
	sportId,
	sportName,
	disciplines,
	discipline,
	search,
}: {
	games: CurrentGamesOption;
	sportId: string;
	sportName: string;
	disciplines: readonly DisciplineOption[];
	discipline: DisciplineOption;
	search: SportSearch;
}) {
	const { t } = useTranslation("meetsList");
	const { readOnly, session } = Route.useRouteContext();
	const navigate = useNavigate();
	const disciplineId = discipline.discipline_id;
	const bulkAllowed = !readOnly && canBulkMeets(session.active_role);
	const medalOn = search.medal === true;

	const go = useCallback(
		(next: Omit<SportSearch, "discipline">) => void navigate({ to: "/sports/$sportId", params: { sportId }, search: { discipline: disciplineId, ...next } }),
		[navigate, sportId, disciplineId],
	);
	const applied = useMemo<SportDraft>(
		() => ({ event: search.event ?? "", day: search.day ?? "", status: search.status ?? "", venue: search.venue ?? "", no: search.no ?? "" }),
		[search.event, search.day, search.status, search.venue, search.no],
	);
	// Filter / Reset write the card's fields; "Medal events only" is the toolbar's and stays as it is.
	const f = useFilterDraft<SportDraft>(
		applied,
		(d) =>
			go({
				event: d.event || undefined,
				day: d.day && isDay(d.day) ? d.day : undefined,
				status: d.status || undefined,
				venue: d.venue || undefined,
				no: meetNoFromDraft(d.no),
				medal: medalOn ? true : undefined,
			}),
		EMPTY_SPORT_DRAFT,
	);
	const activeCount = [search.event, search.day, search.status, search.venue, search.no].filter((v) => v !== undefined).length;

	// What every read of this discipline shares; the Filters card narrows the list within it.
	const scope = { games_id: games.games_id, sport_id: sportId, discipline_id: disciplineId, ...(medalOn ? { medal_events: true } : {}) };
	const status = search.status ? groupFilter(search.status) : undefined;
	const filters = {
		...scope,
		event_id: search.event,
		venue_id: search.venue,
		meet_no: search.no,
		...(search.day ? dayRange(search.day) : {}),
		...(status ? { schedule_status: status.schedule_status, result_status: status.result_status } : {}),
	};
	const list = useInfiniteQuery({ ...scheduleQuery(filters, PAGE), placeholderData: keepPreviousData });
	const meets = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);

	// "Bulk action" (admins): the ticked meets, among those loaded, up to the API's bound. Another filter starts afresh.
	const [picked, setPicked] = useState<ReadonlySet<string>>(() => new Set());
	const listKey = JSON.stringify(filters);
	const [pickedFor, setPickedFor] = useState(listKey);
	if (pickedFor !== listKey) {
		setPickedFor(listKey);
		setPicked(new Set());
	}
	const selection: MeetSelection | undefined = bulkAllowed
		? {
				selected: picked,
				max: MEET_BULK_ACTION_MAX,
				set: (ids, on) =>
					setPicked((prev) => {
						const next = new Set(prev);
						for (const id of ids) {
							if (on && next.size < MEET_BULK_ACTION_MAX) next.add(id);
							else if (!on) next.delete(id);
						}
						return next;
					}),
			}
		: undefined;
	const pickedMeets = useMemo(() => meets.filter((m) => picked.has(m.meet_id)), [meets, picked]);
	// The dialog keeps the meets it opened with: the list reloads (and drops deleted rows) while it shows the answer.
	const [bulkFor, setBulkFor] = useState<readonly MeetListItem[] | null>(null);
	const onBulkDone = (r: MeetBulkActionResult) =>
		setPicked((prev) => {
			const next = new Set(prev);
			for (const id of r.done) next.delete(id);
			return next;
		});
	const [reportsOpen, setReportsOpen] = useState(false);

	// "Add meet" (write roles only). A new key per opening: every opening starts a fresh form (and fresh ids).
	const [adding, setAdding] = useState<number | null>(null);
	// The meets just added, highlighted in the table for a while.
	const [fresh, setFresh] = useState<ReadonlySet<string>>(() => new Set());
	useEffect(() => {
		if (fresh.size === 0) return;
		const timer = setTimeout(() => setFresh(new Set()), FRESH_MS);
		return () => clearTimeout(timer);
	}, [fresh]);
	const onCreated = (c: AddMeetsCreated) => {
		setFresh(new Set(c.meetIds));
		// Show the day the first new meet starts on (the games' calendar), in its discipline, so the new rows are on screen.
		const day = c.firstStart ? dayInZone(c.firstStart, games.timezone) : search.day;
		void navigate({ to: "/sports/$sportId", params: { sportId }, search: { discipline: c.disciplineId, day } });
	};

	return (
		<div className="flex flex-col gap-[1rem]">
			<FilterPanel activeCount={activeCount}>
				<SportFilters
					games={games}
					scope={scope}
					disciplineId={disciplineId}
					draft={f.draft}
					set={f.set}
					onApply={f.apply}
					onReset={f.reset}
					activeCount={activeCount}
				/>
			</FilterPanel>

			<Card className="flex flex-col">
				<div className="flex flex-wrap items-center gap-[0.5rem] border-rule border-b px-[1rem] py-[0.75rem]">
					{!readOnly && (
						<Button size="sm" onClick={() => setAdding((n) => (n ?? 0) + 1)}>
							<svg viewBox="0 0 24 24" className="size-[1.125rem]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
								<path d="M12 5v14M5 12h14" />
							</svg>
							{t("addMeet.button")}
						</Button>
					)}
					{bulkAllowed && (
						<Button variant="outline" size="sm" disabled={pickedMeets.length === 0} onClick={() => setBulkFor(pickedMeets)}>
							<svg viewBox="0 0 16 16" className="size-[1rem]" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
								<path d="M2 4h7M2 8h12M2 12h12M12 2v4M10 4h4" />
							</svg>
							{pickedMeets.length > 0 ? t("bulk.buttonCount", { n: formatInteger(pickedMeets.length) }) : t("bulk.button")}
						</Button>
					)}
					<StandingsPicker key={disciplineId} disciplineId={disciplineId} disciplineName={discipline.name} />
					<Button variant="outline" size="sm" onClick={() => setReportsOpen(true)}>
						<SheetIcon />
						{t("report.button")}
					</Button>
					<MedalToggle
						on={medalOn}
						onToggle={() =>
							go({ event: search.event, day: search.day, status: search.status, venue: search.venue, no: search.no, medal: medalOn ? undefined : true })
						}
					/>
					{meets.length > 0 && (
						<span className="ml-auto text-[0.8125rem] text-muted">{t("list.showing", { count: meets.length, n: formatInteger(meets.length) })}</span>
					)}
				</div>

				{list.isPending ? (
					<div className="flex justify-center px-[1rem] py-[2rem]">
						<Spinner className="size-[1.5rem] text-board" label={t("sport.loading")} />
					</div>
				) : list.isError ? (
					<div className="flex flex-col items-start gap-[0.5rem] px-[1rem] py-[1.25rem]">
						<p className="font-bold text-ink">{t("sport.listError")}</p>
						<p className="text-[0.875rem] text-muted">{errorMessage(list.error)}</p>
						<Button variant="outline" size="sm" onClick={() => void list.refetch()}>
							{t("errors.retry")}
						</Button>
					</div>
				) : meets.length === 0 ? (
					<div className="flex flex-col items-start gap-[0.5rem] px-[1rem] py-[1.5rem]">
						<p className="text-[0.9375rem] text-muted">
							{activeCount > 0
								? t("sport.noMeetsFiltered", { discipline: discipline.name })
								: t(medalOn ? "sport.noMedalMeets" : "sport.noMeets", { discipline: discipline.name })}
						</p>
						{activeCount > 0 && (
							<Button variant="outline" size="sm" onClick={f.reset}>
								{t("empty.reset")}
							</Button>
						)}
					</div>
				) : (
					<>
						<SportMeetsTable
							meets={meets}
							fresh={fresh}
							canWrite={!readOnly}
							selection={selection}
							label={t("sport.listLabel", { discipline: discipline.name })}
						/>
						<div className="flex flex-wrap items-center justify-center gap-[0.75rem] border-rule border-t px-[1rem] py-[0.75rem]">
							{list.hasNextPage ? (
								<Button variant="outline" size="sm" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
									{t("list.more", { n: formatInteger(PAGE) })}
								</Button>
							) : (
								<p className="text-[0.875rem] text-muted">{t("list.allLoaded")}</p>
							)}
						</div>
					</>
				)}
			</Card>

			{bulkAllowed && bulkFor && <BulkMeetsDialog meets={bulkFor} onClose={() => setBulkFor(null)} onDone={onBulkDone} />}
			<ReportsDialog
				open={reportsOpen}
				onClose={() => setReportsOpen(false)}
				meets={meets}
				filters={filters}
				filtered={activeCount > 0 || medalOn}
				gamesName={games.name}
				sportName={sportName}
				disciplineName={discipline.name}
			/>

			{!readOnly && adding !== null && (
				<AddMeetDrawer
					key={adding}
					open
					onClose={() => setAdding(null)}
					games={games}
					sportName={sportName}
					disciplines={disciplines}
					initialDisciplineId={disciplineId}
					day={search.day ?? null}
					onCreated={onCreated}
				/>
			)}
		</div>
	);
}

/**
 * The old OVR's sport filters: Event, Date, Status, Venue, plus Meet no.
 * (typed, exact). Each list is this
 * discipline's own — its events; the days it has meets (`GET /meets/days`,
 * with counts); the games' venues. Applied by the Filter button, never per
 * keystroke.
 */
function SportFilters({
	games,
	scope,
	disciplineId,
	draft,
	set,
	onApply,
	onReset,
	activeCount,
}: {
	games: CurrentGamesOption;
	scope: Parameters<typeof meetDaysQuery>[0];
	disciplineId: string;
	draft: SportDraft;
	set: <K extends keyof SportDraft>(key: K, value: SportDraft[K]) => void;
	onApply: () => void;
	onReset: () => void;
	activeCount: number;
}) {
	const { t } = useTranslation("meetsList");
	const ids = { event: useId(), day: useId(), status: useId(), venue: useId() };
	const events = useQuery(eventOptionsQuery(disciplineId));
	const days = useQuery(meetDaysQuery(scope));
	const venueOptions = useQuery(gamesVenueOptionsQuery(games.games_id));

	const eventList = useMemo<SelectOption[]>(() => (events.data ?? []).map((e) => ({ value: e.event_id, label: e.name, description: e.code })), [events.data]);
	const counts = useMemo(() => new Map((days.data?.items ?? []).map((r) => [r.day, r.count])), [days.data]);
	const dayDates = useMemo(() => (days.data ? daysOf(days.data.items) : []), [days.data]);
	// A day from a link outside the discipline's days is still offered, so the field shows it.
	const dayList = draft.day && !dayDates.includes(draft.day) ? [...dayDates, draft.day].sort() : dayDates;
	const dayOptions = useMemo<SelectOption[]>(
		() =>
			dayList.map((d) => {
				const n = counts.get(d);
				return { value: d, label: formatCalendarDay(d), description: n === undefined ? undefined : t("sport.meets", { count: n, n: formatInteger(n) }) };
			}),
		[dayList, counts, t],
	);
	const statusOptions = useMemo<SelectOption[]>(() => STATUS_GROUPS.map((g) => ({ value: g, label: t(`groups.${g}`) })), [t]);
	// Only the venues this games plays in, each hall without its courts — a hall's filter includes them.
	const venues = useMemo<SelectOption[]>(
		() => topLevelVenues(venueOptions.data ?? []).map((v) => ({ value: v.venue_id, label: v.name, description: v.code })),
		[venueOptions.data],
	);

	return (
		<FilterBar onApply={onApply} onReset={onReset} activeCount={activeCount} aria-label={t("filters.label")} columns="repeat(auto-fill, minmax(min(12rem, 100%), 1fr))">
			<FilterField label={t("filters.event")} htmlFor={ids.event}>
				<Select id={ids.event} options={eventList} value={draft.event || null} placeholder={t("filters.allEvents")} clearable onChange={(v) => set("event", v ?? "")} />
			</FilterField>
			<FilterField label={t("filters.date")} htmlFor={ids.day}>
				<Select
					id={ids.day}
					options={dayOptions}
					value={draft.day || null}
					placeholder={t("filters.allDays")}
					searchable={false}
					clearable
					onChange={(v) => set("day", v ?? "")}
				/>
			</FilterField>
			<FilterField label={t("filters.status")} htmlFor={ids.status}>
				<Select
					id={ids.status}
					options={statusOptions}
					value={draft.status || null}
					placeholder={t("filters.anyStatus")}
					searchable={false}
					clearable
					onChange={(v) => set("status", (v as StatusGroup | null) ?? "")}
				/>
			</FilterField>
			<FilterField label={t("filters.venue")} htmlFor={ids.venue}>
				<Select id={ids.venue} options={venues} value={draft.venue || null} placeholder={t("filters.allVenues")} clearable onChange={(v) => set("venue", v ?? "")} />
			</FilterField>
			<MeetNoField value={draft.no} onChange={(v) => set("no", v)} />
		</FilterBar>
	);
}
